import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  Image, 
  Video, 
  MapPin, 
  Calendar,
  User,
  Shield,
  BarChart3,
  Filter,
  Search,
  Download,
  Eye,
  Edit3,
  Trash2,
  Camera,
  Receipt,
  ShieldCheck,
  Award,
  FileBarChart,
  Plus,
  Clock,
  CheckCircle2,
  AlertCircle,
  Tag
} from 'lucide-react';


const DocumentEvidenceManagement = ({ projectId, user }) => {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'general',
    phase: '',
    activity: '',
    tags: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [projectPhases, setProjectPhases] = useState([]);
  const [projectActivities, setProjectActivities] = useState([]);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    category: '',
    phase: '',
    activity: '',
    tags: ''
  });
  const [newFile, setNewFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
    fetchAnalytics();
    fetchProjectPhases();
    fetchProjectActivities();
  }, [projectId]);

  const fetchProjectPhases = async () => {
    try {
      // Fetch project to get phases
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}`);
      const project = response.data;
      
      // Default phases if not in project
      const defaultPhases = [
        { id: 'initiating', name: 'Initiating' },
        { id: 'planning', name: 'Planning' },
        { id: 'executing', name: 'Executing' },
        { id: 'monitoring', name: 'Monitoring & Controlling' },
        { id: 'closing', name: 'Closing' }
      ];
      
      setProjectPhases(project.phases || defaultPhases);
    } catch (error) {
      console.error('Error fetching phases:', error);
      // Set default phases
      setProjectPhases([
        { id: 'initiating', name: 'Initiating' },
        { id: 'planning', name: 'Planning' },
        { id: 'executing', name: 'Executing' },
        { id: 'monitoring', name: 'Monitoring & Controlling' },
        { id: 'closing', name: 'Closing' }
      ]);
    }
  };

  const fetchProjectActivities = async () => {
    try {
      // Fetch tasks/milestones as activities
      const tasksResponse = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/tasks`);
      const milestonesResponse = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/milestones`);
      
      const tasks = tasksResponse.data.map(task => ({
        id: task.id,
        name: task.title,
        type: 'task'
      }));
      
      const milestones = milestonesResponse.data.map(milestone => ({
        id: milestone.id,
        name: milestone.title,
        type: 'milestone'
      }));
      
      setProjectActivities([...tasks, ...milestones]);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/document-evidence`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/document-evidence/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/document-evidence/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      const tags = (uploadForm.tags || '').split(',').map(tag => tag.trim()).filter(tag => tag);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('phase', uploadForm.phase);
      formData.append('activity', uploadForm.activity);
      formData.append('tags', JSON.stringify(tags));
      
      const response = await axios.post(
        `${BACKEND_URL}/api/projects/${projectId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      setDocuments([...documents, response.data]);
      setUploadDialogOpen(false);
      setUploadForm({
        title: '',
        description: '',
        category: 'general',
        phase: '',
        activity: '',
        tags: ''
      });
      setSelectedFile(null);
      toast.success('Document uploaded successfully');
      fetchAnalytics(); // Refresh analytics
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setEditForm({
      title: document.title || '',
      description: document.description || '',
      category: document.category || 'general',
      phase: document.phase || '',
      activity: document.activity || '',
      tags: (document.tags || []).join(', ')
    });
    setNewFile(null);
    setEditDialogOpen(true);
  };

  const handleUpdateDocument = async () => {
    try {
      const tags = (editForm.tags || '').split(',').map(tag => tag.trim()).filter(tag => tag);
      
      // If there's a new file, use FormData
      if (newFile) {
        const formData = new FormData();
        formData.append('file', newFile);
        formData.append('title', editForm.title);
        formData.append('description', editForm.description);
        formData.append('category', editForm.category);
        formData.append('phase', editForm.phase);
        formData.append('activity', editForm.activity);
        formData.append('tags', JSON.stringify(tags));
        
        await axios.put(
          `${BACKEND_URL}/api/projects/${projectId}/documents/${editingDocument.id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        // Otherwise just update metadata
        const updateData = {
          title: editForm.title,
          description: editForm.description,
          category: editForm.category,
          phase: editForm.phase,
          activity: editForm.activity,
          tags: tags
        };
        
        await axios.put(
          `${BACKEND_URL}/api/projects/${projectId}/documents/${editingDocument.id}`,
          updateData
        );
      }
      
      const statusMsg = user.role === 'social_actor' ? 
        'Document edit submitted for approval!' : 
        'Document updated successfully!';
      toast.success(statusMsg);
      
      setEditDialogOpen(false);
      setEditingDocument(null);
      setNewFile(null);
      
      // Refetch documents to see changes
      await fetchDocuments();
      await fetchAnalytics();
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error(error.response?.data?.detail || 'Failed to update document');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}/document-evidence/${documentId}`);
      setDocuments(documents.filter(doc => doc.id !== documentId));
      toast.success('Document deleted successfully');
      fetchAnalytics(); // Refresh analytics
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const getIconForCategory = (categoryId) => {
    const iconMap = {
      photos_videos: Camera,
      contracts: FileText,
      invoices: Receipt,
      permits: ShieldCheck,
      reports: FileBarChart,
      certificates: Award,
      gps_data: MapPin
    };
    return iconMap[categoryId] || FileText;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Document Evidence Management</h2>
          <p className="text-gray-600">Organize and track project evidence with automatic timestamping</p>
        </div>
        {user.role !== 'investor' && (
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center space-x-2">
                <Upload className="h-4 w-4" />
                <span>Upload Evidence</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Upload Document Evidence</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={uploadForm.title}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter document title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select value={uploadForm.category} onValueChange={(value) => setUploadForm(prev => ({ ...prev, category: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe this evidence document"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="file">Upload File *</Label>
                  <Input
                    id="file"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setSelectedFile(file);
                      // Auto-fill title if empty
                      if (file && !uploadForm.title) {
                        setUploadForm(prev => ({ ...prev, title: file.name }));
                      }
                    }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4,.mov"
                  />
                  {selectedFile && (
                    <p className="text-sm text-gray-500">
                      Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phase">Project Phase</Label>
                    <Select value={uploadForm.phase} onValueChange={(value) => setUploadForm(prev => ({ ...prev, phase: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select phase" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectPhases.map(phase => (
                          <SelectItem key={phase.id} value={phase.id}>{phase.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="activity">Activity</Label>
                    <Select value={uploadForm.activity} onValueChange={(value) => setUploadForm(prev => ({ ...prev, activity: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select activity" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectActivities.length > 0 ? (
                          projectActivities.map(activity => (
                            <SelectItem key={activity.id} value={activity.id}>
                              {activity.name} ({activity.type})
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="general">General Activity</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="e.g., delivery, receipt, equipment"
                  />
                </div>
                
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUploadDocument} disabled={!uploadForm.title || !selectedFile}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Evidence
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Documents</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.total_documents}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="h-5 w-5 text-green-700" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Verified</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.verification_status?.verified || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-yellow-700" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.verification_status?.pending || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Storage Used</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.storage_usage?.total_size_mb ? `${analytics.storage_usage.total_size_mb.toFixed(1)}MB` : '0MB'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center space-x-2 flex-1">
          <Search className="h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search documents, descriptions, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
          />
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDocuments.map(document => {
          const IconComponent = getIconForCategory(document.category);
          return (
            <Card key={document.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2">
                    <IconComponent className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-base line-clamp-1">{document.title}</CardTitle>
                  </div>
                  <Badge className={getStatusColor(document.verification_status)}>
                    {document.verification_status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{document.description}</p>
                
                <div className="space-y-2 mb-4">
                  {document.phase && (
                    <div className="flex items-center text-xs text-gray-500">
                      <Calendar className="h-3 w-3 mr-1" />
                      <span>Phase: {document.phase}</span>
                    </div>
                  )}
                  {document.activity && (
                    <div className="flex items-center text-xs text-gray-500">
                      <span>Activity: {document.activity}</span>
                    </div>
                  )}
                  <div className="flex items-center text-xs text-gray-500">
                    <User className="h-3 w-3 mr-1" />
                    <span>{new Date(document.uploaded_at).toLocaleDateString('en-GB')}</span>
                  </div>
                  {document.file_size > 0 && (
                    <div className="flex items-center text-xs text-gray-500">
                      <span>Size: {formatFileSize(document.file_size)}</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {document.tags && document.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {document.tags.slice(0, 3).map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        <Tag className="h-2 w-2 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                    {document.tags.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{document.tags.length - 3} more
                      </Badge>
                    )}
                  </div>
                )}

                {/* Location Data */}
                {document.metadata?.location_data && (
                  <div className="flex items-center text-xs text-gray-500 mb-3">
                    <MapPin className="h-3 w-3 mr-1" />
                    <span className="truncate">{document.metadata.location_data.address || 'GPS Location Available'}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedDocument(document);
                      setViewDialogOpen(true);
                    }}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    View
                  </Button>
                  
                  {user.role !== 'investor' && (
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditDocument(document)}
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDocument(document.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredDocuments.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || selectedCategory !== 'all' 
                ? 'No documents match your current filters' 
                : 'Upload your first document evidence to get started'}
            </p>
            {user.role !== 'investor' && !searchTerm && selectedCategory === 'all' && (
              <Button onClick={() => setUploadDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Upload Evidence
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Document Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              {selectedDocument && (
                <>
                  {React.createElement(getIconForCategory(selectedDocument.category), { className: 'h-5 w-5' })}
                  <span>{selectedDocument.title}</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedDocument && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Category</Label>
                  <p className="text-sm text-gray-900">
                    {categories.find(cat => cat.id === selectedDocument.category)?.name || selectedDocument.category}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Status</Label>
                  <Badge className={getStatusColor(selectedDocument.verification_status)}>
                    {selectedDocument.verification_status}
                  </Badge>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium text-gray-700">Description</Label>
                <p className="text-sm text-gray-900">{selectedDocument.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {selectedDocument.phase && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Phase</Label>
                    <p className="text-sm text-gray-900">{selectedDocument.phase}</p>
                  </div>
                )}
                {selectedDocument.activity && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Activity</Label>
                    <p className="text-sm text-gray-900">{selectedDocument.activity}</p>
                  </div>
                )}
              </div>
              
              {selectedDocument.tags && selectedDocument.tags.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Tags</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedDocument.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedDocument.metadata?.location_data && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Location</Label>
                  <p className="text-sm text-gray-900">
                    {selectedDocument.metadata.location_data.address || 
                     `${selectedDocument.metadata.location_data.latitude}, ${selectedDocument.metadata.location_data.longitude}`}
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-4 text-xs text-gray-500">
                <div>
                  <Label className="text-sm font-medium text-gray-700">File Size</Label>
                  <p>{formatFileSize(selectedDocument.file_size)}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Uploaded</Label>
                  <p>{new Date(selectedDocument.uploaded_at).toLocaleString('en-GB')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">File Type</Label>
                  <p>{selectedDocument.file_type}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
          </DialogHeader>
          
          {editingDocument && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Document title"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-file">Replace File (Optional)</Label>
                <Input
                  id="edit-file"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setNewFile(file);
                  }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4,.mov"
                />
                {newFile && (
                  <p className="text-sm text-green-700">
                    ✓ New file selected: {newFile.name} ({(newFile.size / 1024).toFixed(2)} KB)
                  </p>
                )}
                {!newFile && editingDocument.file_url && (
                  <p className="text-sm text-gray-500">
                    Current file: {editingDocument.title}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Select value={editForm.category} onValueChange={(value) => setEditForm(prev => ({ ...prev, category: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="photos">Photos</SelectItem>
                    <SelectItem value="receipts">Receipts</SelectItem>
                    <SelectItem value="beneficiary">Beneficiary</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this evidence document"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-phase">Project Phase</Label>
                  <Select value={editForm.phase} onValueChange={(value) => setEditForm(prev => ({ ...prev, phase: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select phase" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectPhases.map(phase => (
                        <SelectItem key={phase.id} value={phase.id}>{phase.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-activity">Activity</Label>
                  <Select value={editForm.activity} onValueChange={(value) => setEditForm(prev => ({ ...prev, activity: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select activity" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectActivities.length > 0 ? (
                        projectActivities.map(activity => (
                          <SelectItem key={activity.id} value={activity.id}>
                            {activity.name} ({activity.type})
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="general">General Activity</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-tags">Tags</Label>
                <Input
                  id="edit-tags"
                  value={editForm.tags}
                  onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="training, beneficiary, receipt, equipment"
                />
              </div>

              {user.role === 'social_actor' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Your changes will be submitted for approval by the Project Manager or Administrator.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateDocument} disabled={!editForm.title}>
                  <Edit3 className="h-4 w-4 mr-2" />
                  Update Document
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentEvidenceManagement;